import { useState, type FormEvent, type ReactElement } from 'react';
import type { SessionMembership } from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n';

// §18: zones, prices, the hold window and the floor; never people.
export function CopyLayoutDialog({
  venueName,
  others,
  onCopy,
  onClose,
  error,
  busy,
}: {
  venueName: string;
  others: readonly SessionMembership[];
  onCopy: (fromVenueId: string) => void;
  onClose: () => void;
  error: string | null;
  busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [from, setFrom] = useState(others[0]?.venueId ?? '');

  // Asked for a copy with nothing to copy from, it copies nothing. §18
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (from !== '') onCopy(from);
  }

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pt-safe pb-safe">
      <button
        type="button"
        aria-label={t('close')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-label={t('copyLayoutTitle')}
        onSubmit={submit}
        className="relative w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl"
      >
        <h2 className="mb-1 text-base font-bold">{t('copyLayoutTitle')}</h2>
        <p className="mb-4 text-xs text-muted-foreground">{venueName}</p>

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="copy-from"
        >
          {t('copyLayoutFrom')}
        </label>
        <select
          id="copy-from"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          className="mb-3 w-full rounded-lg border px-3 py-2 text-base"
        >
          {others.map((each) => (
            <option key={each.venueId} value={each.venueId}>
              {each.venueName} · {each.spotKind}
            </option>
          ))}
        </select>

        <p className="text-[11px] leading-relaxed text-muted-foreground">{t('copyLayoutLead')}</p>

        {error !== null && (
          <p role="alert" className="mt-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('close')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('copy')}
          </Button>
        </div>
      </form>
    </div>
  );
}
